import Button from "@components/buttons/Button"
import AnimatedDash, {
  Container as AnimatedDashContainer,
  animationStyles,
} from "@components/buttons/common/NestedAnimatedDash"
import ColorSwatches, {
  Container as ColorSwatchesContainer,
} from "@components/ColorSwatches"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import ProductTag, {
  Container as ProductTagContainer,
} from "@components/ProductTag"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body16MainStyles,
  smallcapsRegular13Styles,
} from "@constants/typography"
import { getProductURL } from "@utils/urls"
import { Link } from "gatsby"
import * as R from "ramda"
import React from "react"
import styled from "styled-components"

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;

  ${ProductTagContainer} {
    margin-left: 5.84px;
    margin-bottom: 4.32px;
  }

  @media ${breakpoints.laptop} {
    ${ColorSwatchesContainer} {
      margin-top: 0;
    }
  }
`

export const ImageWrapper = styled.div<{
  hoverType?: string
  paddingTop?: boolean
}>`
  position: relative;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
  display: flex;
  padding: 16px;
  transition: background-color ${animation.medium} ${cubicBezier};
  background-color: ${colors.backgroundGray};

  .productBlock__LifestyleImage,
  .productBlock__GalleryImage {
    display: none;
  }

  @media ${breakpoints.laptop} {
    align-self: stretch;
    justify-self: stretch;
    padding: 20px;
    padding-top: ${({ paddingTop }) => (paddingTop ? "48px" : "20px")};

    .productBlock__LifestyleImage,
    .productBlock__GalleryImage {
      position: absolute;
      left: 0;
      bottom: 0;
      display: initial;
      visibility: hidden;

      img {
        align-self: stretch;
        justify-self: stretch;
        padding: 20px;
      }
    }

    &:hover {
      ${({ hoverType }) => {
        switch (hoverType) {
          case "productBlock__GalleryImage":
            return `
              .productBlock__BaseImage {
                visibility: hidden;
              }
              
              .productBlock__GalleryImage {
                visibility: visible !important;
              }
            `
          case "productBlock__LifestyleImage":
            return `
              .productBlock__BaseImage {
                visibility: hidden;
              }
              
              .productBlock__LifestyleImage {
                visibility: visible !important;
              }
            `
          default:
            return `
              background-color: ${colors.backgroundGrayHover};
            `
        }
      }}
    }
  }

  ${ImageHelperContainer} {
    flex-grow: 0;
    flex-shrink: 0;
    width: 100%;
    height: auto;

    img {
      object-fit: contain;
    }
  }
`

const Tags = styled.div`
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  display: flex;
  margin-top: auto;
`

const Content = styled.div`
  width: 100%;
  height: auto;
  min-height: 126px;
  padding-top: 14px;
  background: transparent;

  @media ${breakpoints.laptop} {
    min-height: 102px;
  }

  @media ${breakpoints.largeDesktop} {
    min-height: 82px;
  }
`

const Row = styled.div`
  display: flex;
  margin-bottom: 4px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  ${AnimatedDashContainer} {
    margin-left: 12px;
    margin-right: 2px;
    flex-shrink: 0;
  }
`

const Title = styled.div`
  ${body16MainStyles}
`

const Subtitle = styled.div`
  ${smallcapsRegular13Styles}
  display: inline-block;
  opacity: 0.3;
`

const DecoratedSubtitle = styled(Subtitle)`
  margin-right: 12px;
  text-decoration: line-through;
`

const GatsbyLink = styled(Link)`
  ${({ to }) => (!to.length ? "pointer-events: none" : "cursor: pointer")};
  ${animationStyles}

  @media ${breakpoints.laptop} {
    flex-grow: 1;
    display: flex;
    height: 100%;
    max-height: calc(100% - 38px);
  }
`

const Swatches = styled.div`
  display: none;

  @media ${breakpoints.laptop} {
    flex-direction: column;
    flex-grow: 0;
    flex-shrink: 0;
    margin-bottom: 20px;
  }
`

const ActionButton = styled(Button)`
  display: none;
  height: 56px;
`

const BlockContainer = styled.div<{ left: number }>`
  ${animationStyles}

  @media ${breakpoints.laptop} {
    flex-grow: 1;
    display: flex;
    height: 100%;
    max-height: calc(100% - 38px);

    &:hover {
      z-index: 9999;
      position: absolute;
      left: ${({ left }) => left}px;
      transform: translate(-24px, -24px);
      width: calc(25vw + 48px);
      height: auto;
      max-height: initial;
      padding: 28px;
      background-color: ${colors.offWhite};
      border: solid 1px ${colors.backgroundGrayHover};
      border-radius: 2px;
      box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.04);
      cursor: pointer;

      ${Content} {
        min-height: initial;
        margin-bottom: 14px;
      }

      ${Swatches} {
        display: flex;
      }

      ${ActionButton} {
        display: flex;
      }
    }
  }
`

export interface ProductBlockWithFormProps {
  __typename: string
  containerRef: React.RefObject<HTMLDivElement>
  customPrint?: boolean
  productName: string
  slug: string
  variants: Array<{
    color: string
    compareAtPrice?: string
    hexString?: string
    images: Array<string>
    language?: string
    name: string
    onAddToCart: (quantity: number) => void
    onClick: () => void
    price: string
    size?: string
    sku: string
    swatchImage?: string
  }>
}

const ProductBlockWithForm: React.FC<ProductBlockWithFormProps> = ({
  __typename,
  containerRef,
  customPrint,
  productName,
  slug,
  variants,
}) => {
  const componentRef = React.createRef<HTMLDivElement>()
  const hasSize = variants.some((variant) => variant.size)
  const hasLanguage = variants.some((variant) => variant.language)
  const showForm =
    !hasSize ||
    !hasLanguage ||
    (customPrint && __typename === "ContentfulProduct")

  const [activeVariant, setActiveVariant] = React.useState(variants[0])

  const getSwatchClickHandler = (variant) => () => {
    setActiveVariant(variant)
  }

  const swatchColors = React.useMemo(
    () =>
      R.uniqBy(
        ({ hexString, swatchImage }: any) => swatchImage || hexString,
        variants.map((variant) => ({
          ...R.pick(["name", "hexString", "swatchImage"])(variant),
          onClick: getSwatchClickHandler(variant),
        }))
      ) as any,
    [variants]
  )

  const price = activeVariant?.price
  const compareAtPrice = activeVariant?.compareAtPrice
  const showCompareAtPrice = price && compareAtPrice && price !== compareAtPrice
  const productLink =
    customPrint || __typename === "ContentfulProductVariant"
      ? getProductURL(slug, activeVariant.sku)
      : getProductURL(slug)

  if (!activeVariant.images || activeVariant.images.length === 0) {
    return null
  }

  const [left, setLeft] = React.useState(0)

  React.useEffect(() => {
    if (componentRef?.current) {
      setLeft(componentRef?.current.getBoundingClientRect().left)
    }
  }, [componentRef, variants])

  const onScroll = () => {
    if (containerRef?.current && componentRef?.current) {
      setLeft(componentRef?.current.getBoundingClientRect().left)
    }
  }

  React.useEffect(() => {
    if (containerRef?.current && componentRef?.current) {
      containerRef?.current.addEventListener("scroll", onScroll)
      window.addEventListener("resize", onScroll)
    }

    return () => {
      if (containerRef?.current && componentRef?.current) {
        containerRef?.current.removeEventListener("scroll", onScroll)
        window.removeEventListener("resize", onScroll)
      }
    }
  }, [containerRef, componentRef, variants])

  return showForm ? (
    <BlockContainer left={left} ref={componentRef}>
      <Container>
        <GatsbyLink
          onClick={(e) => {
            e.stopPropagation()
            activeVariant.onClick()
          }}
          to={productLink}
        >
          <ImageWrapper
          // hoverType={
          //   !!lifestyleImage
          //     ? "productBlock__LifestyleImage"
          //     : !!galleryImage
          //     ? "productBlock__GalleryImage"
          //     : undefined
          // }
          // paddingTop={paddingTop}
          >
            {/* {tags && tags.length > 0 && (
              <Tags>
                {tags.map((tag) => (
                  <ProductTag key={tag.text} {...tag} />
                ))}
              </Tags>
            )} */}
            <ImageHelper
              className="productBlock__BaseImage"
              src={activeVariant.images[0]}
            />
            {/* {lifestyleImage && (
              <ImageHelper
                className="productBlock__LifestyleImage"
                src={lifestyleImage!}
              />
            )} */}
            {/* {galleryImage && (
              <ImageHelper
                className="productBlock__GalleryImage"
                src={galleryImage!}
              />
            )} */}
          </ImageWrapper>
        </GatsbyLink>
        <Content>
          <Row>
            <Title>{productName}</Title>
            <AnimatedDash />
          </Row>
          {showCompareAtPrice && (
            <DecoratedSubtitle>{compareAtPrice}</DecoratedSubtitle>
          )}
          <Subtitle>{price}</Subtitle>
        </Content>
        <Swatches>
          <ColorSwatches colors={swatchColors} />
        </Swatches>
        <ActionButton
          className="addToCart"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            activeVariant.onAddToCart(1)
          }}
          text={"Add to Cart"}
        />
      </Container>
    </BlockContainer>
  ) : (
    <GatsbyLink
      onClick={(e) => {
        e.stopPropagation()
        activeVariant.onClick()
      }}
      to={productLink}
    >
      <Container>
        <ImageWrapper
        // hoverType={
        //   !!lifestyleImage
        //     ? "productBlock__LifestyleImage"
        //     : !!galleryImage
        //     ? "productBlock__GalleryImage"
        //     : undefined
        // }
        // paddingTop={paddingTop}
        >
          {/* {tags && tags.length > 0 && (
            <Tags>
              {tags.map((tag) => (
                <ProductTag key={tag.text} {...tag} />
              ))}
            </Tags>
          )} */}
          <ImageHelper
            className="productBlock__BaseImage"
            src={activeVariant.images[0]}
          />
          {/* {lifestyleImage && (
            <ImageHelper
              className="productBlock__LifestyleImage"
              src={lifestyleImage!}
            />
          )} */}
          {/* {galleryImage && (
            <ImageHelper
              className="productBlock__GalleryImage"
              src={galleryImage!}
            />
          )} */}
        </ImageWrapper>
        <Content>
          <Row>
            <Title>{productName}</Title>
            <AnimatedDash />
          </Row>
          {showCompareAtPrice && (
            <DecoratedSubtitle>{compareAtPrice}</DecoratedSubtitle>
          )}
          <Subtitle>{price}</Subtitle>
        </Content>
      </Container>
    </GatsbyLink>
  )
}

export default ProductBlockWithForm
